import React from "react";
import {
  Card,
  H1,
  H3,
  Text,
  Button,
  ControlGroup,
  HTMLSelect,
  Tag,
  Spinner,
  NonIdealState,
  Icon,
  InputGroup,
  ButtonGroup,
  Divider,
} from "@blueprintjs/core";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { DateInput } from "@blueprintjs/datetime";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { inject, observer } from "mobx-react";
import { apiCallGet, apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import moment from "moment";
import { withRouter } from "react-router-dom";
import { getStatusMeta as getStatusMetaGlobal, getDisplayStatusMeta as getDisplayStatusMetaGlobal } from "../../constants/statusColors";
import { getIsBilledLabel, getIsBilledPill } from "../../utils/isBilledPill";

class IPDBillRegister extends React.Component {
  state = {
    // Data
    bills: [],
    allBills: [], // Store all data for filtering
    branches: [],
    doctors: [],

    // Loading states
    isLoading: false,
    isGenerating: false,

    // Filters
    fromDate: new Date(),
    toDate: new Date(),
    selectedBranch: "",
    selectedDoctor: "",
    selectedBranchName: null,
    selectedDoctorName: null,
    reportType: "Master", // Only Master report

    // AG Grid state
    gridApi: null,
    gridColumnApi: null,
    quickFilterText: "",
  };

  componentDidMount = async () => {
    await this.loadInitialData();
  };

  componentDidUpdate(prevProps) {
    if (prevProps.authStore?.selectedBranchName !== this.props.authStore?.selectedBranchName) {
      const preferredName = this.props.authStore?.selectedBranchName;
      if (!preferredName) {
        this.setState({
          selectedBranch: "",
          selectedBranchName: null,
        });
        return;
      }
      const match = (this.state.branches || []).find(
        (b) => (b.name || "").toLowerCase() === String(preferredName).toLowerCase()
      );
      if (match && match._id !== this.state.selectedBranch) {
        this.setState({
          selectedBranch: match._id,
          selectedBranchName: match.name,
        });
      }
    }
  }

  loadInitialData = async () => {
    try {
      this.setState({ isLoading: true });
      const [branches, doctors] = await Promise.all([this.loadBranches(), this.loadDoctors()]);

      // Set default branch from global selection if available, else first branch
      if (branches && branches.length > 0) {
        const preferredName = this.props.authStore?.selectedBranchName;
        const norm = (s) =>
          String(s || "")
            .trim()
            .toLowerCase();
        const match = preferredName ? branches.find((b) => norm(b.name) === norm(preferredName)) : null;
        this.setState(
          {
            selectedBranch: (match && match._id) || "ALL_BRANCHES",
            selectedBranchName: (match && match.name) || null,
          },
          async () => {
            // Auto-generate once branch is set
            await this.generateReport();
          }
        );
      } else {
        cogoToast.warn("No branches available. Please contact administrator.");
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      cogoToast.error("Failed to load initial data");
      this.setState({ bills: [] });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadBranches = async () => {
    try {
      // Use the existing branch master API
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllSites`, {});

      // Normalize different response structures to an array of {_id, name}
      const rawBranches = response?.data || response || [];
      const branches = (Array.isArray(rawBranches) ? rawBranches : [])
        .map((site, idx) => ({
          _id:
            site?._id ||
            site?.id ||
            site?.siteId ||
            site?.value ||
            site?.key ||
            (typeof site === "string" ? site : String(idx)),
          name:
            site?.site ||
            site?.siteName ||
            site?.name ||
            site?.branchName ||
            site?.label ||
            (typeof site === "string" ? site : `Site ${idx + 1}`),
        }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      console.log("🔍 DEBUG: Branches loaded:", branches.length);
      this.setState({ branches });
      return branches;
    } catch (err) {
      console.error("Error loading branches:", err);
      // Try alternative API endpoint
      try {
        const response = await apiCallPost(`/meta-sx/${this.props.authStore.prefix}/branchMaster/getAll`, {});
        const rawBranches = response?.data || response || [];
        const branches = (Array.isArray(rawBranches) ? rawBranches : [])
          .map((site, idx) => ({
            _id: site?._id || site?.id || String(idx),
            name: site?.site || site?.siteName || site?.name || site?.branchName || site?.label || `Site ${idx + 1}`,
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        this.setState({ branches });
        return branches;
      } catch (fallbackErr) {
        console.error("Fallback branch API also failed:", fallbackErr);
        // Return dummy branches if both APIs fail
        const dummyBranches = [
          { _id: "branch1", name: "Barrackpore" },
          { _id: "branch2", name: "Kolkata" },
          { _id: "branch3", name: "Howrah" },
          { _id: "branch4", name: "Dum Dum" },
        ];
        this.setState({ branches: dummyBranches });
        return dummyBranches;
      }
    }
  };

  loadDoctors = async () => {
    try {
      // Use the existing doctor/consultant API
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllConsultants`, {});

      // Transform consultants to doctor format
      const doctors = Array.isArray(response)
        ? response.map((consultant) => ({
            _id: consultant._id || consultant.id,
            name: consultant.name || consultant.consultantName,
            specialization: consultant.specialization || consultant.department,
            phone: consultant.phone || consultant.contactNumber,
            email: consultant.email || consultant.emailId,
            shortcode: consultant.shortcode || consultant.code,
          }))
        : [];

      console.log("🔍 DEBUG: Doctors/Consultants loaded:", doctors.length);
      this.setState({ doctors });
      return doctors;
    } catch (err) {
      console.error("Error loading doctors:", err);
      // Try alternative API endpoint
      try {
        const response = await apiCallPost(`/meta-sx/${this.props.authStore.prefix}/doctorMaster/getAll`, {});
        const doctors = response?.data || response || [];
        this.setState({ doctors });
        return doctors;
      } catch (fallbackErr) {
        console.error("Fallback doctor API also failed:", fallbackErr);
        // Return dummy doctors if both APIs fail
        const dummyDoctors = [
          { _id: "doc1", name: "DR. ARIJIT MITRA" },
          { _id: "doc2", name: "DR. ARNAB DAS" },
          { _id: "doc3", name: "DR. SUDIPTA ROY" },
          { _id: "doc4", name: "DR. PRITAM SARKAR" },
        ];
        this.setState({ doctors: dummyDoctors });
        return dummyDoctors;
      }
    }
  };

  generateReport = async () => {
    // No need to validate branch selection since "All Branches" is now a valid option

    try {
      this.setState({ isGenerating: true });

      // Build payload with optional branch filter
      const payload = {
        reportType: "MASTER",
        // Use user-selected date range instead of hardcoded wide range
        startDate: moment(this.state.fromDate).format("YYYY-MM-DD"),
        endDate: moment(this.state.toDate).format("YYYY-MM-DD"),
        // Remove rate limit - set high limit to fetch all records (30000 or unlimited)
        limit: 30000,
        page: 1,
        // Try to remove limit entirely if backend supports it
        noLimit: true,
      };

      // Only add branch filter if a specific branch is selected (not "All Branches")
      if (this.state.selectedBranch && this.state.selectedBranch !== "ALL_BRANCHES") {
        // API expects branchName (actual name), not branch ID
        const selectedBranchObj = this.state.branches.find((b) => b._id === this.state.selectedBranch);
        const branchName = selectedBranchObj?.name;
        if (branchName) {
          payload.branch = branchName; // Send actual branch name, not ID
        }
      }

      console.log("🔍 DEBUG: Branch mapping:", {
        selectedBranchId: this.state.selectedBranch,
        isAllBranches: this.state.selectedBranch === "ALL_BRANCHES",
        branchFilterApplied: !!payload.branch,
        branchNameSent: payload.branch,
      });

      // Fetch all data using POST
      const endpoint = `/v3/${this.props.authStore.prefix}/reports/ipdBillRegister`;
      console.log("🔍 DEBUG: POST Endpoint:", endpoint, "Payload:", payload);
      const resp = await apiCallPost(endpoint, payload);

      console.log("🔍 DEBUG: API Response:", resp);

      // Handle the correct API response structure based on the provided schema
      const raw = resp?.data?.items || [];
      const rows = Array.isArray(raw) ? raw : [];

      console.log("🔍 DEBUG: Extracted rows:", rows.length, rows.slice(0, 2));

      // Map to display schema and store all bills
      const mapped = rows.map((item) => this.mapItemToRow(item));

      console.log("🔍 DEBUG: Mapped data:", mapped.length, mapped.slice(0, 2));

      console.log("🔍 DEBUG: Setting state with mapped data:", mapped.length);

      this.setState(
        {
          allBills: mapped,
          bills: mapped, // Store all data
        },
        () => {
          console.log(
            "🔍 DEBUG: State updated - bills:",
            this.state.bills.length,
            "allBills:",
            this.state.allBills.length
          );
        }
      );

      // Apply current filters and sorting
      this.applyFilters();

      if (mapped.length > 0) {
        cogoToast.success(`Report generated • ${mapped.length} total records`);
      } else {
        cogoToast.warn("No data found for the selected criteria");
      }
    } catch (err) {
      console.error("Error generating IPD Bill Register:", err);

      // Handle different types of API errors
      if (err?.response?.status === 304) {
        // 304 means data hasn't changed - use existing data and apply filters
        console.log("🔍 DEBUG: 304 Not Modified - filtering existing data");

        // If we have existing data, just apply the current filters
        if (this.state.allBills && this.state.allBills.length > 0) {
          this.applyFilters();
          cogoToast.success(`Using cached data • ${this.state.allBills.length} total records`);
        } else {
          // No existing data, show warning
          cogoToast.warn("Data not modified since last request, but no cached data available.");
        }
      } else if (err?.response?.status === 404) {
        cogoToast.error("Report endpoint not found. Please contact administrator.");
        this.setState({ bills: [], allBills: [] });
      } else if (err?.response?.status >= 500) {
        cogoToast.error("Server error occurred. Please try again later.");
        this.setState({ bills: [], allBills: [] });
      } else {
        cogoToast.error(
          `Failed to generate report: ${err?.response?.data?.message || err?.message || "Unknown error"}`
        );
        this.setState({ bills: [], allBills: [] });
      }
    } finally {
      this.setState({ isGenerating: false });
    }
  };

  handleDateChange = (field, date) => {
    this.setState({ [field]: date });
  };

  handleFilterChange = (field, value) => {
    this.setState({ [field]: value });
  };

  // Helper function to create branch items for dropdown
  createBranchSelectItems = () => {
    return this.state.branches.map((branch) => branch.name).filter(Boolean);
  };

  // Helper function to create doctor items for dropdown with shortcode formatting
  createDoctorSelectItems = () => {
    return this.state.doctors
      .map((doctor) => {
        const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
        const name = doctor.name;
        return `${shortcode}${name}`;
      })
      .filter(Boolean);
  };

  // Helper function to get formatted doctor name
  getFormattedDoctorName = (doctor) => {
    if (!doctor) return null;
    const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
    const name = doctor.name;
    return `${shortcode}${name}`;
  };

  // Helper function to extract doctor name from formatted string
  extractDoctorNameFromFormatted = (formattedName) => {
    if (!formattedName) return null;
    // Remove shortcode part (everything before " | ")
    const withoutShortcode = formattedName.includes(" | ") ? formattedName.split(" | ")[1] : formattedName;
    return withoutShortcode.trim();
  };

  // Handle branch selection from SingleSelectWrapper
  handleBranchFilterSelect = (branchName) => {
    if (branchName === null || branchName === undefined) {
      // "All Branches" selected - use special value to indicate all branches
      this.setState({
        selectedBranch: "ALL_BRANCHES",
        selectedBranchName: null,
      });
    } else {
      const selectedBranch = this.state.branches.find((branch) => branch.name === branchName);
      this.setState({
        selectedBranch: selectedBranch ? selectedBranch._id : "",
        selectedBranchName: branchName || null,
      });
    }
  };

  // Handle doctor selection from SingleSelectWrapper
  handleDoctorFilterSelect = (formattedDoctorName) => {
    // Find the doctor by matching the formatted name
    const selectedDoctor = this.state.doctors.find((doctor) => {
      const formatted = this.getFormattedDoctorName(doctor);
      return formatted === formattedDoctorName;
    });

    console.log("🔍 DEBUG: Doctor filter selection:", {
      formattedDoctorName,
      selectedDoctor,
      extractedName: this.extractDoctorNameFromFormatted(formattedDoctorName),
    });

    this.setState(
      {
        selectedDoctor: selectedDoctor ? selectedDoctor._id : "",
        selectedDoctorName: formattedDoctorName || null,
      },
      () => {
        // Apply filtering immediately if we have data
        if (this.state.allBills.length > 0) {
          this.applyFilters();
        }
      }
    );
  };

  applyFilters = () => {
    const { allBills, selectedDoctor } = this.state;

    console.log("🔍 DEBUG: applyFilters called - API should have filtered date/branch, only applying doctor filter");

    if (!allBills || allBills.length === 0) {
      console.log("🔍 DEBUG: No allBills data, setting bills to empty");
      this.setState({ bills: [] });
      return;
    }

    let filteredBills = [...allBills];
    console.log("🔍 DEBUG: Starting with bills (already filtered by API):", filteredBills.length);

    // Only apply doctor filter client-side (API doesn't handle doctor filtering)
    if (selectedDoctor && this.state.selectedDoctorName) {
      const extractedDoctorName = this.extractDoctorNameFromFormatted(this.state.selectedDoctorName);
      console.log("🔍 DEBUG: Applying doctor filter:", extractedDoctorName);

      filteredBills = filteredBills.filter((bill) => {
        const billDoctorName = bill["Doctor Name"] || bill._doctorName || "";
        // Try multiple matching strategies
        const matches = [
          // Exact match
          billDoctorName === extractedDoctorName,
          // Match without "DR. " prefix
          billDoctorName.replace(/^DR\.\s*/i, "") === extractedDoctorName.replace(/^DR\.\s*/i, ""),
          // Case-insensitive match
          billDoctorName.toLowerCase() === extractedDoctorName.toLowerCase(),
          // Partial match (contains)
          billDoctorName.toLowerCase().includes(extractedDoctorName.toLowerCase()),
          // Also check _doctorId match
          bill._doctorId === selectedDoctor,
        ].some(Boolean);

        if (!matches) {
          console.log("🔍 DEBUG: Bill filtered out by doctor:", bill.PID, "doctorName:", billDoctorName);
        }

        return matches;
      });

      console.log("🔍 DEBUG: After doctor filter:", filteredBills.length);
    }

    // Sort by date (newest first) before displaying
    const sortedBills = this.sortByDate(filteredBills);

    console.log("🔍 DEBUG: Final sorted bills:", sortedBills.length);

    this.setState({ bills: sortedBills }, () => {
      console.log("🔍 DEBUG: Bills state updated after filtering:", this.state.bills.length);
    });

    if (selectedDoctor && this.state.selectedDoctorName && filteredBills.length !== allBills.length) {
      const extractedDoctorName = this.extractDoctorNameFromFormatted(this.state.selectedDoctorName);
      cogoToast.success(`Filtered to ${filteredBills.length} of ${allBills.length} records for ${extractedDoctorName}`);
    }
  };

  sortByDate = (bills) => {
    return [...bills].sort((a, b) => {
      // Use scheduledDate as primary, then createdAt, then formatted date
      const getDateForSorting = (bill) => {
        const scheduledDate = bill._originalItem?.scheduledDate;
        const createdAt = bill._originalItem?.createdAt || bill._createdAt;

        if (scheduledDate) {
          return moment(scheduledDate);
        } else if (createdAt) {
          return moment(createdAt);
        } else {
          return moment(bill.Date, "DD-MMM-YYYY");
        }
      };

      const dateA = getDateForSorting(a);
      const dateB = getDateForSorting(b);

      // Sort descending (newest first)
      return dateB.valueOf() - dateA.valueOf();
    });
  };

  clearFilters = () => {
    this.setState({
      // Don't clear selectedBranch as it's mandatory
      selectedDoctor: "",
      fromDate: new Date(),
      toDate: new Date(),
      quickFilterText: "",
    });
    cogoToast.success("Filters cleared (click Generate to apply)");
  };

  hasActiveFilters = () => {
    const { selectedDoctor, fromDate, toDate } = this.state;
    const today = moment().format("YYYY-MM-DD");
    const defaultFromDate = today;
    const fromDateStr = moment(fromDate).format("YYYY-MM-DD");
    const toDateStr = moment(toDate).format("YYYY-MM-DD");

    // Branch is not considered an "active filter" since it's mandatory
    return selectedDoctor !== "" || fromDateStr !== defaultFromDate || toDateStr !== today;
  };

  formatCurrency = (num) => {
    if (num === null || typeof num === "undefined") return "-";
    try {
      return num.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
    } catch (e) {
      return `${num}`;
    }
  };

  // Helper function to calculate totals for numeric columns
  calculateTotals = (data) => {
    const numericFields = [
      "Packages(Rate)",
      "Special Discount",
      "MOU Discount",
      "IOL Discount",
      "Total Receivable",
      "Authorization",
      "Net Payable",
      "Collected",
      "Balance",
    ];

    const totals = {};

    // Initialize totals
    numericFields.forEach((field) => {
      totals[field] = 0;
    });

    // Calculate sums
    (data || []).forEach((row) => {
      numericFields.forEach((field) => {
        const value = Number(row?.[field] || 0);
        if (!isNaN(value)) {
          totals[field] += value;
        }
      });
    });

    return totals;
  };

  // Helper function to format currency totals for PDF
  formatCurrencyForTotal = (amount) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
  };

  // Helpers to normalize API/dummy data → table rows
  getNumber = (...candidates) => {
    for (const c of candidates) {
      if (typeof c === "number" && !Number.isNaN(c)) return c;
      if (typeof c === "string" && c.trim() !== "" && !Number.isNaN(Number(c))) return Number(c);
    }
    return 0;
  };

  // Helper function to safely convert paise to rupees
  paiseToRupees = (paise) => {
    const num = this.getNumber(paise);
    return num / 100;
  };

  // Helper function to get latest authorization
  getLatestAuthorization = (item) => {
    const entries = item?.authorization?.entries || [];
    if (!Array.isArray(entries) || entries.length === 0) {
      return null;
    }

    const activeIndex = item?.authorization?.activeIndex;
    if (typeof activeIndex === "number" && activeIndex >= 0 && activeIndex < entries.length) {
      return entries[activeIndex];
    }

    // If no active index, return the most recent entry
    return entries.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0))[0];
  };

  // Helper function to get latest invoice
  getLatestInvoice = (item) => {
    const invoices = item?.invoices || [];
    if (!Array.isArray(invoices) || invoices.length === 0) {
      return null;
    }

    // Return the most recent invoice
    return invoices.sort((a, b) => new Date(b.generatedAt || 0) - new Date(a.generatedAt || 0))[0];
  };

  // Helper function to format payment events from latest invoice
  formatPaymentCollection = (item) => {
    const latestInvoice = this.getLatestInvoice(item);
    const payments = latestInvoice?.payments || item?.payments?.events || [];

    if (!Array.isArray(payments) || payments.length === 0) {
      return "-";
    }

    // Sort events by timestamp (newest first)
    const sortedEvents = payments.sort((a, b) => {
      const dateA = new Date(a.ts || 0);
      const dateB = new Date(b.ts || 0);
      return dateB - dateA;
    });

    return sortedEvents
      .map((event) => {
        const amount = (event.amountPaise || event.amount || 0) / (event.amountPaise ? 100 : 1);
        const mode = event.mode || "Unknown";
        const moneyReceiptNo = event.moneyReceiptNo || "No Receipt";
        const date = event.ts ? moment(event.ts).format("DD/MM/YYYY") : "Unknown";
        const prefix = event.type === "Refund" ? "-" : "+";
        const type = event.type || "Payment";

        // Format: [Type] [+/-Amount] [Mode] [Receipt] [Date]
        return `${type}: ${prefix}${amount} | ${mode} | ${moneyReceiptNo} | ${date}`;
      })
      .join("\n");
  };

  // Derive meta based on item
  getDisplayStatusMeta = (item) => {
    const status = item?.status || item?.bookingStatus || item?.workflowStatus || item?.surgeryStatus || "-";
    const payorPanel = item?.payorPanel || "";
    const isBilled = item?.isBilled ?? false;
    return getDisplayStatusMetaGlobal({ status, payorPanel, isBilled });
  };

  renderStatusCell = (item, fallbackLabel) => {
    const meta = this.getDisplayStatusMeta(item || {});
    const label = meta?.label || fallbackLabel || "-";
    return `<span style="display:inline-flex;align-items:center;justify-content:center;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;color:${meta.color};background:${meta.background};border:1px solid ${meta.border};text-transform:none;line-height:1;">${label}</span>`;
  };
  renderIsBilledCell = (row) => getIsBilledPill(row);

  mapItemToRow = (item) => {
    console.log("🔍 DEBUG: Mapping item:", item);

    // Test the mapping with sample data structure
    if (process.env.NODE_ENV === "development") {
      console.log("🔍 DEBUG: New API structure mapping test");
      console.log("- patientId:", item?.patientId);
      console.log("- patientName:", item?.patientName);
      console.log("- doctorName:", item?.doctorName);
      console.log("- bookingReference:", item?.bookingReference);
      console.log("- payorPanel:", item?.payorPanel);
      console.log("- authorization entries:", item?.authorization?.entries?.length || 0);
      console.log("- invoices count:", item?.invoices?.length || 0);
    }

    // Get latest invoice and authorization
    const latestInvoice = this.getLatestInvoice(item);
    const latestAuth = this.getLatestAuthorization(item);
    const consolidatedBilling = latestInvoice?.consolidatedBilling || item?.consolidatedBilling || {};

    // Calculate values based on new API structure
    const surgeryRatePaise = this.getNumber(consolidatedBilling?.surgeryRatePaise);
    const anaestheticChargesPaise = this.getNumber(consolidatedBilling?.anaestheticChargesPaise);
    const tissueProcessingPaise = this.getNumber(consolidatedBilling?.tissueProcessingPaise);
    const femtoChargesPaise = this.getNumber(consolidatedBilling?.femtoChargesPaise);
    const specialDiscountPaise = this.getNumber(consolidatedBilling?.specialDiscountPaise);
    const mouDiscountPaise = this.getNumber(consolidatedBilling?.mouDiscountPaise);
    const iolDiscountPaise = this.getNumber(consolidatedBilling?.iolDiscountPaise);

    // Convert paise to rupees - ensure 0 instead of negative values
    const packages = Math.max(0, (surgeryRatePaise + anaestheticChargesPaise + tissueProcessingPaise) / 100);
    const specialDiscount = Math.max(0, specialDiscountPaise / 100);
    const mouDiscount = Math.max(0, mouDiscountPaise / 100);
    const iolDiscount = Math.max(0, iolDiscountPaise / 100);
    const femtoCharges = Math.max(0, femtoChargesPaise / 100);

    // Calculate total receivable - ensure 0 minimum
    const totalReceivable = Math.max(0, packages + femtoCharges - specialDiscount - mouDiscount - iolDiscount);

    // Get authorization amount - ensure 0 if not available
    const authorization = Math.max(0, this.getNumber(latestAuth?.amountRupees));

    // Calculate patient payable (Net Payable) - ensure 0 minimum
    const netPayable = Math.max(0, totalReceivable - authorization);

    // Get payment totals
    const totalPaid = this.getNumber(item?.payments?.totalPaid, item?.payments?.totalPaidPaise / 100);
    const totalRefunded = this.getNumber(item?.payments?.totalRefunded, item?.payments?.totalRefundedPaise / 100);
    const collected = totalPaid - totalRefunded;

    // Calculate balance
    const balance = netPayable - collected;

    const statusMeta = this.getDisplayStatusMeta(item || {});
    const statusLabel = statusMeta.label;

    // Master report row structure using new API response fields
    return {
      _id: item?._id || item?.id || item?.bookingReference || "-",
      Date: item?.scheduledDate
        ? moment(item.scheduledDate).format("DD-MMM-YYYY")
        : item?.createdAt
        ? moment(item.createdAt).format("DD-MMM-YYYY")
        : "-",
      PID: item?.patientGenericPid || item?.patientId || item?.patientOriginalData?.sysPid || "-",
      "Patient Name": item?.patientName || item?.patientOriginalData?.name || "-",
      "Doctor Name": item?.doctorName ? item.doctorName.replace(/^(Dr\.|DR\.)\s*/i, "") : "-",
      "Service Id": item?.bookingReference || "-",
      Status: statusLabel,
      Payor: item?.payorPanel || "-",
      "TPA Name": latestAuth?.tpaName || "-",
      "Insurance Name": latestAuth?.insuranceName || "-",
      "Packages(Rate)": packages,
      "Special Discount": specialDiscount,
      "MOU Discount": mouDiscount,
      "IOL Discount": iolDiscount,
      "Total Receivable": totalReceivable,
      Authorization: authorization,
      "Payment Collection": this.formatPaymentCollection(item),
      "Net Payable": netPayable,
      Collected: collected,
      Balance: balance,
      "Invoice No": latestInvoice?.number || "-",
      "Billed By": item?.markedBilledBy?.name || latestInvoice?.generatedBy?.name || "-",

      // Additional fields for filtering (not displayed but used for filter logic)
      _originalItem: item, // Keep reference to original item for filtering
      _branchId: item?.branchId || "",
      _branchName: item?.branchName || "",
      _doctorId: item?.doctorId || "",
      _doctorName: item?.doctorName || "",
      _createdAt: item?.scheduledDate || item?.createdAt,
    };
  };

  getColumnDefs = () => {
    const currencyFormatter = (params) => {
      const value = Number(params.value || 0);
      return `₹${value.toLocaleString("en-IN")}`;
    };

    // Master report columns only
    return [
      {
        headerName: "PID",
        field: "PID",
        width: 120,
        pinned: "left",
        cellStyle: { fontWeight: "600" },
      },
      {
        headerName: "Patient Name",
        field: "Patient Name",
        width: 180,
        pinned: "left",
      },
      {
        headerName: "Doctor Name",
        field: "Doctor Name",
        width: 180,
      },
      {
        headerName: "Surgery Id",
        field: "Service Id",
        width: 120,
        cellRenderer: (params) => {
          const value = params.value;
          if (!value || value === "-") return "-";

          const container = document.createElement("div");
          const link = document.createElement("span");

          link.innerHTML = value;
          link.style.color = "#137cbd";
          link.style.cursor = "pointer";
          link.style.textDecoration = "underline";
          link.style.fontWeight = "600";
          link.onclick = () => this.navigateToDetails(params.data);

          container.appendChild(link);
          return container;
        },
      },
      {
        headerName: "Status",
        field: "Status",
        width: 130,
        filter: "agSetColumnFilter",
        filterValueGetter: (params) => this.getDisplayStatusMeta(params.data?._originalItem || {}).label,
        valueFormatter: (params) => this.getDisplayStatusMeta(params.data?._originalItem || {}).label,
        cellRenderer: (params) => this.renderStatusCell(params.data?._originalItem, params.value),
        cellStyle: { textAlign: "center", fontWeight: "600" },
      },
      {
        headerName: "Is Billed",
        field: "isBilled",
        width: 120,
        filter: "agSetColumnFilter",
        valueGetter: (params) => getIsBilledLabel(params.data?._originalItem || params.data),
        valueFormatter: (params) => getIsBilledLabel(params.data?._originalItem || params.data),
        cellRenderer: (params) => this.renderIsBilledCell(params.data?._originalItem || params.data),
        cellStyle: { textAlign: "center", fontWeight: "600" },
      },
      {
        headerName: "Payor",
        field: "Payor",
        width: 120,
      },
      {
        headerName: "TPA Name",
        field: "TPA Name",
        width: 140,
      },
      {
        headerName: "Insurance Name",
        field: "Insurance Name",
        width: 150,
      },
      {
        headerName: "Packages",
        field: "Packages(Rate)",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Special Discount",
        field: "Special Discount",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "MOU Discount",
        field: "MOU Discount",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "IOL Discount",
        field: "IOL Discount",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Total Receivable",
        field: "Total Receivable",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Authorization",
        field: "Authorization",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Net Payable",
        field: "Net Payable",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Payment Collection",
        field: "Payment Collection",
        width: 350,
        cellRenderer: (params) => {
          if (!params.value || params.value === "-") return "-";
          const lines = params.value.split("\n");
          return `<div style="white-space: pre-wrap; line-height: 1.5; font-size: 12px; padding: 4px;">${lines
            .map(
              (line) =>
                `<div style="margin-bottom: 2px; border-bottom: 1px dotted #555; padding-bottom: 2px;">${line}</div>`
            )
            .join("")}</div>`;
        },
        autoHeight: true,
      },
      {
        headerName: "Collected",
        field: "Collected",
        width: 120,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Balance",
        field: "Balance",
        width: 120,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Invoice No",
        field: "Invoice No",
        width: 120,
      },
      {
        headerName: "Billed By",
        field: "Billed By",
        width: 150,
      },
    ];
  };

  // Navigate to surgery details page
  navigateToDetails = (surgery) => {
    const detailsPath = `/v3/workflow/surgery-booking/${surgery._id || surgery.docNo || surgery.bookingReference}`;
    window.open(detailsPath, "_blank", "noopener,noreferrer");
  };

  // AG Grid event handlers
  onGridReady = (params) => {
    this.setState({
      gridApi: params.api,
      gridColumnApi: params.columnApi,
    });
  };

  // Get only visible/displayed columns (respects sidebar column selection)
  getDisplayedColumns = () => {
    if (!this.state.gridColumnApi) return this.getColumnDefs();
    const allColumns = this.state.gridColumnApi.getAllColumns();
    if (!allColumns) return this.getColumnDefs();
    const visibleColumns = allColumns.filter((col) => col.isVisible());
    return visibleColumns.map((col) => {
      const colDef = col.getColDef();
      return {
        headerName: colDef.headerName,
        field: colDef.field,
        ...colDef,
      };
    });
  };

  onQuickFilterChanged = (event) => {
    this.setState({ quickFilterText: event.target.value });
  };

  exportToCSV = () => {
    // Collect filtered data from grid if available
    let data = [];
    if (this.state.gridApi) {
      this.state.gridApi.forEachNodeAfterFilter((node) => {
        data.push(node.data);
      });
    } else {
      data = this.state.bills || [];
    }

    // Get only visible columns (respects sidebar column selection)
    const columns = this.getDisplayedColumns();
    const totals = this.calculateTotals(data);

    const headers = columns.map((c) => c.headerName).join(",");

    const bodyRows = (data || [])
      .map((row) => {
        return columns
          .map((col) => {
            let value = row[col.field];
            const s = value === null || typeof value === "undefined" ? "" : `${value}`;
            const escaped = s.replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(",");
      })
      .join("\n");

    const totalsRow = columns
      .map((col, idx) => {
        const field = col.field;
        if (Object.prototype.hasOwnProperty.call(totals, field)) {
          return `${totals[field].toLocaleString("en-IN")}`;
        }
        // Put TOTAL label under the first pinned-left column (PID)
        if (field === "PID") {
          return "TOTAL";
        }
        return "";
      })
      .join(",");

    const csvContent = `${headers}\n${bodyRows}${bodyRows ? "\n" : ""}${totalsRow}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `ipd-bill-register-${this.state.reportType.toLowerCase()}-${moment().format("YYYYMMDD-HHmm")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    cogoToast.success("CSV exported successfully with totals!");
  };

  exportToPDF = () => {
    // Get filtered data from AG Grid
    let filteredData = [];
    if (this.state.gridApi) {
      // Get all filtered rows from AG Grid
      this.state.gridApi.forEachNodeAfterFilter((node) => {
        filteredData.push(node.data);
      });
    } else {
      // Fallback to state data if grid API not available
      filteredData = this.state.bills || [];
    }

    // Get only visible columns (respects sidebar column selection)
    const columns = this.getDisplayedColumns();
    const rows = filteredData;

    const branchName =
      this.state.selectedBranch === "ALL_BRANCHES"
        ? "All Branches"
        : this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name || "All Branches";
    const doctorName = this.state.selectedDoctorName
      ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
      : null;

    const win = window.open("", "_blank");
    if (!win) {
      cogoToast.error("Popup blocked. Allow popups to export PDF");
      return;
    }

    const styles = `
      <style>
        @page { size: A4 landscape; margin: 0.5in; }
        body { font-family: Arial, sans-serif; padding: 8px; margin: 0; }
        h2 { margin: 0 0 8px 0; font-size: 16px; }
        .meta { color: #555; margin-bottom: 12px; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 10px; }
        th, td { border: 1px solid #999; padding: 4px; text-align: left; vertical-align: top; }
        th { background: #eee; font-weight: bold; }
        .currency { text-align: right; }
        .wrap { word-wrap: break-word; max-width: 120px; }
      </style>
    `;

    const headerHtml = `
      <h2>IPD Bill Register - ${this.state.reportType}</h2>
      <div class="meta">${branchName}${doctorName ? ` • ${doctorName}` : ""} - ${moment(this.state.fromDate).format(
      "DD-MMM-YYYY"
    )} to ${moment(this.state.toDate).format("DD-MMM-YYYY")} | ${rows.length} records</div>
    `;

    const thead = `<tr>${columns.map((c) => `<th>${c.headerName}</th>`).join("")}</tr>`;
    const tbody = rows
      .map(
        (r) =>
          `<tr>${columns
            .map((c) => {
              const v = r[c.field];
              const value = v === null || typeof v === "undefined" ? "" : v;

              // Apply CSS classes based on column type
              let className = "";
              if (
                c.type === "numericColumn" ||
                c.field.includes("Rate") ||
                c.field.includes("Discount") ||
                c.field.includes("Receivable") ||
                c.field.includes("Authorization") ||
                c.field.includes("Payable") ||
                c.field.includes("Collected") ||
                c.field.includes("Balance")
              ) {
                className = "currency";
              }
              if (c.field === "Payment Collection" || c.field === "Patient Name" || c.field === "Doctor Name") {
                className += " wrap";
              }

              return `<td class="${className.trim()}">${value}</td>`;
            })
            .join("")}</tr>`
      )
      .join("");

    // Calculate totals and create totals row
    const totals = this.calculateTotals(rows);
    const totalsRow = `<tr style="background: #f0f0f0; font-weight: bold;">${columns
      .map((c) => {
        const field = c.field;
        if (field === "PID") {
          return `<td style="font-weight: bold;">TOTAL</td>`;
        }
        if (Object.prototype.hasOwnProperty.call(totals, field)) {
          return `<td style="font-weight: bold; text-align: right;">${this.formatCurrencyForTotal(totals[field])}</td>`;
        }
        return `<td></td>`;
      })
      .join("")}</tr>`;

    win.document.write(
      `<!doctype html><html><head><meta charset="utf-8"/>${styles}</head><body>${headerHtml}<table>${thead}${tbody}${totalsRow}</table></body></html>`
    );
    win.document.close();
    win.focus();
    win.print();
    cogoToast.success("PDF opened for printing");
  };

  renderFilters = () => {
    return (
      <Card
        elevation={2}
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-surface)",
          border: "1px solid var(--border-color)",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
          <Icon icon="filter" style={{ marginRight: "10px", color: "var(--text-primary)" }} />
          <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Filters</H3>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
            <Tag intent="success" large>
              {this.state.bills.length} Records
            </Tag>
          </div>
        </div>

        {/* First row - Main filters */}
        <div style={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap", marginBottom: "15px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              From
            </Tag>
            <DateInput
              value={this.state.fromDate}
              onChange={(d) => this.handleDateChange("fromDate", d)}
              formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
              parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
              placeholder="From Date"
              style={{ width: "140px" }}
              rightElement={<Icon icon="calendar" size={14} />}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              To
            </Tag>
            <DateInput
              value={this.state.toDate}
              onChange={(d) => this.handleDateChange("toDate", d)}
              formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
              parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
              placeholder="To Date"
              style={{ width: "140px" }}
              rightElement={<Icon icon="calendar" size={14} />}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Branch *
            </Tag>
            <div style={{ width: "160px" }}>
              <SingleSelectWrapper
                items={[`All Branches (${this.state.branches.length})`, ...this.createBranchSelectItems()]}
                selectedItem={this.state.selectedBranchName || `All Branches (${this.state.branches.length})`}
                placeholder="Search branches..."
                onChange={(value) => {
                  if (value === `All Branches (${this.state.branches.length})`) {
                    this.handleBranchFilterSelect(null);
                  } else {
                    this.handleBranchFilterSelect(value);
                  }
                }}
                width="100%"
              />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Doctor
            </Tag>
            <div style={{ width: "180px" }}>
              <SingleSelectWrapper
                items={[`All Doctors (${this.state.doctors.length})`, ...this.createDoctorSelectItems()]}
                selectedItem={this.state.selectedDoctorName || `All Doctors (${this.state.doctors.length})`}
                placeholder="Search doctors..."
                onChange={(value) => {
                  if (value === `All Doctors (${this.state.doctors.length})`) {
                    this.handleDoctorFilterSelect(null);
                  } else {
                    this.handleDoctorFilterSelect(value);
                  }
                }}
                width="100%"
              />
            </div>
          </div>
        </div>

        {/* Second row - Quick Filter and Actions */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "15px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Quick Filter
            </Tag>
            <InputGroup
              placeholder="Search all columns..."
              value={this.state.quickFilterText}
              onChange={this.onQuickFilterChanged}
              leftIcon="search"
              style={{ width: "300px" }}
              small
            />
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <Button intent="primary" icon="search" onClick={this.generateReport} loading={this.state.isGenerating}>
              Generate
            </Button>
            <Button icon="filter-remove" onClick={this.clearFilters} disabled={!this.hasActiveFilters()}>
              Clear Filters
            </Button>
            <Button icon="document" onClick={this.exportToPDF} disabled={!this.state.bills.length}>
              PDF
            </Button>
            <Button icon="th" onClick={this.exportToCSV} disabled={!this.state.bills.length}>
              CSV
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  renderTable = () => {
    console.log(
      "🔍 DEBUG: renderTable called with bills:",
      this.state.bills?.length || 0,
      "isGenerating:",
      this.state.isGenerating
    );

    if (this.state.isGenerating) {
      return (
        <NonIdealState
          icon={<Spinner />}
          title="Generating Report..."
          description="Please wait while we fetch the bill data"
        />
      );
    }

    if (!this.state.bills || this.state.bills.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-secondary)",
            backgroundColor: "var(--bg-secondary)",
            borderRadius: "8px",
            border: "1px solid var(--border-color)",
          }}
        >
          <Icon icon="th" size={48} style={{ marginBottom: "15px", opacity: 0.5 }} />
          <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>No records found</div>
          <div style={{ fontSize: "14px", opacity: 0.8 }}>Try adjusting your date range or filters</div>
        </div>
      );
    }

    return (
      <div
        className="ag-theme-alpine-dark"
        style={{
          height: "600px",
          width: "100%",
          borderRadius: "8px",
          border: "1px solid var(--border-color)",
          backgroundColor: "var(--bg-surface)",
        }}
      >
        <AgGridReact
          rowData={this.state.bills}
          columnDefs={this.getColumnDefs()}
          onGridReady={this.onGridReady}
          quickFilterText={this.state.quickFilterText}
          animateRows={true}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          enableBrowserTooltips={true}
          enableRangeSelection={true}
          enableCellTextSelection={true}
          sideBar={{
            toolPanels: [
              {
                id: "columns",
                labelDefault: "Columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivots: true,
                  suppressPivotMode: true,
                  suppressColumnFilter: false,
                  suppressColumnSelectAll: false,
                  suppressColumnExpandAll: false,
                },
              },
              {
                id: "filters",
                labelDefault: "Filters",
                labelKey: "filters",
                iconKey: "filter",
                toolPanel: "agFiltersToolPanel",
                toolPanelParams: {
                  suppressExpandAll: false,
                  suppressFilterSearch: false,
                },
              },
            ],
            defaultToolPanel: null, // No panel open by default
            hiddenByDefault: false,
            position: "right",
          }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 80,
            menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
          }}
          sortingOrder={["desc", "asc"]}
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[25, 50, 100, 200]}
          suppressPaginationPanel={false}
          domLayout="normal"
          getRowId={(params) => params.data._id || params.data["PID"] || Math.random()}
          rowHeight={45}
          headerHeight={40}
        />
      </div>
    );
  };

  renderInfoPanel = () => {
    return (
      <Card
        elevation={1}
        style={{
          padding: "15px",
          backgroundColor: "var(--bg-secondary)",
          border: "1px solid var(--border-color)",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <Icon icon="info-sign" style={{ marginRight: "8px", color: "var(--intent-primary)" }} />
          <Text
            style={{
              color: "var(--text-primary)",
              margin: 0,
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            IPD Bill Register - Master Report
          </Text>
        </div>
      </Card>
    );
  };

  renderHeader = () => {
    const branchName =
      this.state.selectedBranch === "ALL_BRANCHES"
        ? "All Branches"
        : this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name || "All Branches";
    const doctorName = this.state.selectedDoctorName
      ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
      : null;
    return (
      <div
        style={{
          textAlign: "center",
          marginBottom: "20px",
          borderBottom: "2px solid var(--border-color)",
          paddingBottom: "12px",
        }}
      >
        <H1 style={{ color: "var(--text-primary)", margin: 0, fontSize: "24px" }}>
          IPD BILL REGISTER - {this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}
        </H1>
        <Text style={{ color: "var(--text-secondary)", margin: "5px 0", fontSize: "16px" }}>
          {branchName}
          {doctorName ? ` • ${doctorName}` : ""} - {moment(this.state.fromDate).format("DD-MMM-YYYY")} to{" "}
          {moment(this.state.toDate).format("DD-MMM-YYYY")}
        </Text>
        <Text style={{ color: "var(--text-secondary)", margin: "5px 0", fontSize: "14px" }}>
          Master Bill Register Report
        </Text>
      </div>
    );
  };

  render() {
    if (this.state.isLoading) {
      return <NonIdealState icon={<Spinner />} title="Loading..." description="Preparing filters and data" />;
    }
    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            {/* <Icon icon="list-detail-view" style={{ marginRight: "12px", color: "var(--intent-primary)" }} /> */}
            <H1 style={{ color: "var(--text-primary)", margin: 0 }}>IPD Bill Register</H1>
          </div>
        </div>

        {/* {this.renderInfoPanel()} */}
        {this.renderFilters()}

        <Card
          elevation={2}
          style={{
            padding: "20px",
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
          }}
        >
          {this.renderHeader()}
          {this.renderTable()}
        </Card>
      </div>
    );
  }
}

export default inject("authStore")(observer(withRouter(IPDBillRegister)));
